import { useEffect } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Phone, MessageCircle, MapPin, Clock, Mail, ArrowUpRight } from "lucide-react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { t } from "@/lib/i18n";
import { useSeoHead } from "@/lib/head";
import { useSite } from "@/context/SiteContext";
import { Breadcrumbs, ContentSections, FaqSection } from "@/components/public/PageParts";
import LeadForm from "@/components/public/LeadForm";
import OpenNowBadge from "@/components/public/OpenNowBadge";
import { HOURS_LABEL } from "@/lib/businessHours";
import PreviewBanner from "@/components/public/PreviewBanner";
import NotFound from "./NotFound";

export default function EntityPage() {
  const { lang, slug } = useParams();
  const { setSwitcher } = useSite();
  const [sp] = useSearchParams();
  const preview = sp.get("preview") === "1";

  const { data: page, isError, isLoading } = useQuery({
    queryKey: ["page", lang, slug, preview ? "prev" : "pub"],
    queryFn: async () => preview
      ? (await api.get(`/preview/page/${lang}/${slug}`)).data
      : (await api.get(`/public/page/${lang}/${slug}`)).data,
    retry: false,
  });

  useSeoHead(page);
  useEffect(() => { if (page?.switcher) setSwitcher(page.switcher); return () => setSwitcher(null); }, [page, setSwitcher]);

  if (isLoading) return <div className="container-x py-32 text-subtle">…</div>;
  if (isError || !page) return <NotFound />;

  const c = page.content;
  const meta = page.meta || {};
  const isBranch = page.type === "branch";

  return (
    <article>
      {page.preview && <PreviewBanner lang={lang} status={page.preview_meta?.status} updatedAt={page.preview_meta?.updated_at} />}
      {/* HERO */}
      <header className="relative min-h-[46vh] flex items-end">
        {page.image && <img src={page.image} alt={page.seo?.image_alt || ""} className="absolute inset-0 w-full h-full object-cover" />}
        <div className="absolute inset-0 bg-gradient-to-t from-obsidian via-obsidian/75 to-obsidian/30" />
        <div className="relative container-x pb-12 pt-28">
          <Breadcrumbs crumbs={c.breadcrumbs} dir={page.dir} />
          <h1 data-testid="entity-h1" className="text-4xl sm:text-5xl font-extrabold uppercase tracking-tight text-ink mt-5 max-w-3xl">
            {c.h1}
          </h1>
        </div>
      </header>

      <div className="container-x py-12 md:py-16 grid grid-cols-1 lg:grid-cols-3 gap-12">
        <div className="lg:col-span-2">
          <p className="text-lg text-subtle leading-relaxed max-w-3xl">{c.intro}</p>
          <ContentSections sections={c.sections} />

          {page.related?.links?.length > 0 && (
            <section className="mt-12" data-testid="related-links">
              <h2 className="text-2xl font-bold uppercase tracking-tight text-ink mb-6">{page.related.heading}</h2>
              <div className="flex flex-wrap gap-3">
                {page.related.links.map((l, i) => (
                  <Link key={i} to={l.path.replace(/\/$/, "") || l.path} data-testid={`related-link-${i}`}
                    className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full border border-white/15 text-sm text-ink hover:border-copper hover:text-copper transition-colors">
                    {l.anchor} <ArrowUpRight size={14} />
                  </Link>
                ))}
              </div>
            </section>
          )}

          {isBranch && (
            <section className="mt-12" data-testid="branch-contact">
              <h2 className="text-2xl font-bold uppercase tracking-tight text-ink mb-6">{t(lang, "contactInfo")}</h2>
              <ul className="space-y-4 text-subtle">
                {meta.address && <li className="flex gap-3"><MapPin className="text-copper shrink-0" size={18} /> {meta.maps_url ? <a href={meta.maps_url} target="_blank" rel="noopener noreferrer" className="hover:text-copper" data-testid="branch-address-link">{meta.address}</a> : meta.address}</li>}
                {meta.phone && <li className="flex gap-3"><Phone className="text-copper shrink-0" size={18} /> <a href={`tel:${meta.phone}`} className="hover:text-copper">{meta.phone}</a></li>}
                {meta.email && <li className="flex gap-3"><Mail className="text-copper shrink-0" size={18} /> <a href={`mailto:${meta.email}`} className="hover:text-copper">{meta.email}</a></li>}
                <li className="flex items-center gap-3" data-testid="branch-hours"><Clock className="text-copper shrink-0" size={18} /> <span dir="ltr">{meta.hours || HOURS_LABEL}</span> <OpenNowBadge /></li>
                {meta.whatsapp && <li><a href={`https://wa.me/${meta.whatsapp.replace(/\D/g, "")}`} target="_blank" rel="noopener noreferrer" className="btn-copper !py-2 !px-5 mt-2"><MessageCircle size={16} /> WhatsApp</a></li>}
              </ul>
            </section>
          )}

          <FaqSection faq={c.faq} title={t(lang, "faqTitle")} />
        </div>

        {/* Sticky quote */}
        <aside className="lg:sticky lg:top-24 h-fit" id="quote">
          <p className="eyebrow mb-3">{t(lang, "quoteTitle")}</p>
          <LeadForm defaultService={page.type === "service" ? c.h1 : ""} defaultBranch={isBranch ? c.h1 : ""} />
        </aside>
      </div>
    </article>
  );
}
